/* eslint-disable @typescript-eslint/no-floating-promises */
import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import { SortDirections } from '../../../utils/sorting';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import { Modal } from '../../common/Modal/Modal';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterSelect,
    EditPanelSection,
    ISelectOption,
    PanelBody,
    PanelButtonLink,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { UsedEquipmentDto } from '../../FlightLog/interfaces';
import { Spinner } from '../../spinners';
import { EquipmentExpirationLabel } from '../../utils/equipmentExpirationLabel';

type Props = {
    surveyId: string;
    data: UsedEquipmentDto[];
    isLoading: boolean;
    reload: () => Promise<void>;
};

export function EquipmentUsage(props: Props) {
    const translate = useTranslate();
    const [isEquipmentSelectVisible, setIsEquipmentSelectVisible] = React.useState(false);
    const navigate = useNavigate();

    async function removeEquipmentUsage(equipmentUsageId: string) {
        await openApiFetchDelete('/api/equipmentUsage/{equipmentUsageId}', { equipmentUsageId });
        await props.reload();
    }

    async function saveEquipmentUsage(equipmentId: string) {
        await openApiFetchPost('/api/equipment/{equipmentId}/equipmentUsage', { equipmentId }, { surveyId: props.surveyId });
        await props.reload();
    }

    const showEquipmentSelect = () => {
        setIsEquipmentSelectVisible(true);
    };

    return (
        <>
            {(props.isLoading && <Spinner />) || (
                <>
                    <PanelBody noPadding>
                        <Table hover>
                            <TableHead>
                                <TableHeadRow light={true}>
                                    <TableHeader>{translate('common.name')}</TableHeader>
                                    <TableHeader>{translate('common.type')}</TableHeader>
                                    <TableHeader>{translate('common.status')}</TableHeader>
                                    <TableHeader align="right">{translate('equipment.expirationDate')}</TableHeader>
                                    <TableHeader>&nbsp;</TableHeader>
                                </TableHeadRow>
                            </TableHead>
                            <TableBody>
                                {props.data.map((item) => (
                                    <TableRow key={item.id} onClick={() => navigate(`/equipment/${item.equipment.id}`)}>
                                        <TableData>
                                            {item.equipment.equipmentType.isPayload && <i className="fa fa-long-arrow-right ml20 mr20" />}{' '}
                                            {item.equipment.name}{' '}
                                        </TableData>
                                        <TableData> {item.equipment.equipmentType.name} </TableData>
                                        <TableData> {translate(`equipment.${item.equipment.status}`)}</TableData>
                                        <TableData alignRight>
                                            {' '}
                                            <EquipmentExpirationLabel data={item.equipment} />
                                        </TableData>
                                        <TableButtons>
                                            {!item.equipment.equipmentType.isPayload && (
                                                <IconRemoveButton
                                                    removeQuestion={translate('common.confirmDelete', item.equipment.name)}
                                                    onClick={async () => removeEquipmentUsage(item.id)}
                                                />
                                            )}
                                        </TableButtons>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </PanelBody>
                    <EditPanelButtons footer>
                        <PanelButtonLink
                            onClick={() => showEquipmentSelect()}
                            small={true}
                            text={translate('common.addItem', translate('equipment.equipment'))}
                            mt
                            primary={true}
                            icon="fa fa-plus"
                        />
                    </EditPanelButtons>
                </>
            )}

            {isEquipmentSelectVisible && <EquipmentSelect onClose={() => setIsEquipmentSelectVisible(false)} onSelected={saveEquipmentUsage} />}
        </>
    );
}

const getParamsForRequest = (equipmentTypeId: string | undefined) => ({
    query: {
        equipmentTypeId,
        limit: '100',
        offset: '0',
        sortColumns: [
            {
                columnName: 'name',
                direction: SortDirections.Ascending,
            },
        ],
    },
});

function EquipmentSelect(props: { onClose: () => void; onSelected: (selectedEquipmentId: string) => void }) {
    const translate = useTranslate();
    const [equipmentTypes, setEquipmentTypes] = React.useState<ISelectOption[]>([]);
    const [equipment, setEquipment] = React.useState<ISelectOption[]>([]);
    const [equipmentTypeId, setEquipmentTypeId] = React.useState<undefined | string>();
    const [equipmentId, setEquipmentId] = React.useState<undefined | string>();

    React.useEffect(() => {
        (async () => {
            const responseEquipmentTypes = await openApiFetchGet('/api/equipmentTypes', { payload: 'false' });
            if (responseEquipmentTypes) {
                setEquipmentTypes(responseEquipmentTypes.map((item: { id: string; name: string }) => ({ text: item.name, value: item.id })));
            }
        })();
    }, []);

    React.useEffect(() => {
        (async () => {
            const params = getParamsForRequest(equipmentTypeId);
            const responseEquipment = await openApiFetchGet('/api/equipment', params.query);

            if (responseEquipment) {
                const allowedEquipment = responseEquipment.items
                    .filter((item) => !item.equipmentType.isPayload)
                    .map((item) => ({ text: item.name, value: item.id }));

                setEquipment(allowedEquipment);
            }
        })();
    }, [equipmentTypeId]);

    const handleSaveClicked = () => {
        if (equipmentId) {
            props.onClose();
            props.onSelected(equipmentId);
        }
    };

    return (
        <Modal id="copy-dialog" onClose={() => props.onClose()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('equipment.usedEquipment')}</span>
                </h3>
            </Modal.Title>
            <Modal.Content style={{ height: '200px' }}>
                <div className="admin-form" style={{ width: '100%' }}>
                    <EditPanel>
                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="equipmentTypeId"
                                value={equipmentTypeId}
                                handleChange={(value) => setEquipmentTypeId(value)}
                                topLabel={translate('equipment.types')}
                                placeholder={translate('common.selectItem', translate('equipment.types'))}
                                options={equipmentTypes}
                            />
                        </EditPanelSection>
                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="equipmentId"
                                value={equipmentId}
                                handleChange={(value) => setEquipmentId(value)}
                                topLabel={translate('equipment.equipment')}
                                placeholder={translate('common.selectItem', translate('equipment.equipment'))}
                                options={equipment}
                            />
                        </EditPanelSection>
                    </EditPanel>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.close')} onClick={() => props.onClose()} />
                    <EditPanelButton small={true} text={translate('common.save')} onClick={() => handleSaveClicked()} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}
